import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {Backend} from "@generated-backend-types";
import {debounceTime, Observable, of, Subject, switchMap, takeUntil} from "rxjs";
import {SimpleFormGroup} from "@browsepedia/ng-forms";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {map, take} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import { HttpErrorResponse } from "@angular/common/http";
import {NGXLogger} from "ngx-logger";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {NotEnoughPermissionsError} from "../../../../errors/NotEnoughPermissionsError";
import {ContentHeaderService} from "../../../../services/content-header.service";
import {TitleService} from "../../../../services/title.service";
import {LocaleService} from "../../../../services/locale.service";
import {OrdersService} from "../../../../services/api/services/guest-management/orders.service";
import {FormErrorService} from "../../../../services/form-error.service";
import {EmailService} from "../../../../services/api/services/administrations/email.service";
import EmailTypeEnum = Backend.App.Enums.Administrations.EmailTypeEnum;
import Email = Backend.App.Http.Resources.Dashboard.Administrations.Email;
import Order = Backend.App.Http.Resources.Dashboard.Orders.Order;
import DashboardOrderEmailData = Backend.App.Data.Orders.DashboardOrderEmailData;
import OrderEmail = Backend.App.Http.Resources.Ticketshop.Orders.Email.OrderEmail;
import Guest = Backend.App.Http.Resources.Dashboard.Guest;
import {CoreModule} from "../../../core.module";
import {
  MultiSelectWithServiceComponent
} from "../form-inputs/multi-select-with-service/multi-select-with-service.component";
import {FormGroupComponent} from "../form-inputs/form-group/form-group.component";
import {MarkdownEditorComponent} from "../form-inputs/markdown-editor/markdown-editor.component";
import CollectorCase = Backend.App.Http.Resources.Dashboard.Collectors.CollectorCase;
import {CasesService} from "../../../../services/api/services/guest-management/collector/cases.service";


@Component({
    selector: 'app-email-builder-modal',
    templateUrl: './email-builder-modal.component.html',
    imports: [
      CoreModule,
      ReactiveFormsModule,
      MultiSelectWithServiceComponent,
      FormGroupComponent,
      MarkdownEditorComponent
    ],
    standalone: true
})
export class EmailBuilderModalComponent implements OnInit, OnChanges, OnDestroy {
  public previewLoading = false;
  public orderEmail?: OrderEmail;

  @Input() selectedEmailTypes?: EmailTypeEnum | string;
  @Input() order?: Order;
  @Input() case?: CollectorCase;
  @Input() guest?: Guest;
  @Input() locale?: 'de' | 'en';
  @Input() errors?: HttpErrorResponse;
  @Input() allowPreview: boolean = false;
  @Input() submitButtonText = '';
  @Input() emailOverride: string | null = null;
  @Input() previewRequestOverride?: (event: DashboardOrderEmailData) => Observable<string>;
  @Output() submitEvent: EventEmitter<DashboardOrderEmailData> = new EventEmitter<DashboardOrderEmailData>();
  @Output() closeEvent: EventEmitter<void> = new EventEmitter<void>();


  @ViewChild('emailPreviewModal') protected emailPreviewModalTemplateRef?: TemplateRef<string>;

  protected form: SimpleFormGroup<DashboardOrderEmailData> = this.fb.group({
    subject: this.fb.nonNullable.control(
      '',
      [Validators.required, Validators.minLength(1)]
    ),
    pre_content: this.fb.control(
      '',
      [Validators.required]
    ),
    post_content: this.fb.control(
      '',
      [Validators.required]
    ),
    cc_recipients: this.fb.array<string[]>([], {updateOn: 'change'}),
    bcc_recipients: this.fb.array<string[]>([], {updateOn: 'change'}),
    email_id: this.fb.control(
      '',
      [Validators.required]
    )
  }, {updateOn: 'change'}) as unknown as SimpleFormGroup<DashboardOrderEmailData>;

  protected emailTypes = EmailTypeEnum;

  protected emailTemplateIdControl: FormControl = new FormControl<Email[]>([], []);

  protected previewHtmlModalRef?: BsModalRef;
  protected previewHtml: SafeHtml = '';

  private email?: Email;

  private onDestroy$: Subject<void> = new Subject<void>();

  public ngOnInit() {
    this.emailTemplateIdControl.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(100),
      switchMap(
        (value: Email[]) => {
          if (value && value.length > 0 && value[0]?.id) {
            this.email = value[0];
            this.form.controls.email_id.setValue(this.email.id);
            if(this.order?.id) {
              return this.orderService.getOrderEmail(this.order.id, this.email.id).pipe(
                map(r => r.data)
              );
            } else {
              const effectiveLocale: 'de' | 'en' = this.locale ?? (this.guest?.locale as 'de' | 'en') ?? 'de';
              return this.emailService.get(this.email.id).pipe(
                map(res => {
                  return {
                    id: res.data.id,
                    type: res.data.type,
                    localized_subject: res.data.subject[effectiveLocale],
                    localized_pre_content: res.data.pre_content[effectiveLocale],
                    localized_content: res.data.content[effectiveLocale],
                    localized_post_content: res.data.post_content[effectiveLocale],
                  } as OrderEmail;
                })
              );
            }
          }
          return of(null);
        })
    ).subscribe({
      next: data => {
        if (data) {
          this.orderEmail = data;
            this.form.controls.subject.patchValue(data.localized_subject);
            this.form.controls.pre_content.patchValue(data.localized_pre_content);
            this.form.controls.post_content.patchValue(data.localized_post_content);
          }
          this.cdr.markForCheck();
        }
      });
    this.cdr.markForCheck();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if('errors' in changes && changes['errors'].currentValue !== changes['errors'].previousValue) {
      if(
        this.errors?.error &&
        this.errors?.error['errors']
      ) {
        this.cdr.detach();
        this.form.markAllAsTouched();
        if(
          Object.keys(this.errors.error['errors']).find(key => key.startsWith('custom_email_data'))
        ) {
          const formErrors = this.errors;
          Object.entries(formErrors.error['errors']).forEach(item => {
            formErrors.error['errors'][item[0].replace('custom_email_data.', '')] = item[1]
          });
          this.formErrorService.mapErrorToForm(this.form, formErrors);
        } else {
          this.formErrorService.mapErrorToForm(this.form, this.errors);
        }
        this.cdr.reattach();
      }
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  /**
   * Loads and opens the preview Modal
   */
  public showPreview() {
    if (this.previewRequestOverride) {
      this.previewLoading = true;

      const data = this.form.getRawValue();
      data.pre_content = this.prepareMarkdownForBackend(data.pre_content ?? '');
      data.post_content = this.prepareMarkdownForBackend(data.post_content ?? '');

      this.previewRequestOverride(data).subscribe({
        next: res => {
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(res);
          this.previewHtmlModalRef = this.modalService.show(this.emailPreviewModalTemplateRef!, {class: 'order-send-email-preview-modal'});
          this.cdr.markForCheck();
          this.previewHtmlModalRef.onHide?.pipe(take(1)).subscribe({
            next: () => {
              this.previewHtml = '';
              this.cdr.markForCheck();
            }
          });
          this.cdr.markForCheck()
        },
        error: err => {
          if (err instanceof NotEnoughPermissionsError) {
            this.toastrService.error(this.translate.instant('shared.misc.not_enough_permissions_error'));
          } else if (err instanceof HttpErrorResponse) {
            this.cdr.detach();
            this.form.markAllAsTouched();
            this.formErrorService.mapErrorToForm(this.form, err);
            this.cdr.reattach();
          } else {
            this.log.error(JSON.stringify(err));
            this.toastrService.error(
              this.translate.instant('guest_management.orders.send_email.preview_error')
            );
          }
          this.previewLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.cdr.detectChanges()
          }, 100)
        },
        complete: () => {
          this.previewLoading = false
          this.cdr.markForCheck();
        }
      });
      this.cdr.markForCheck();
    } else if (this.orderEmail && this.order && this.email && !this.previewLoading && this.emailPreviewModalTemplateRef) {
      this.previewLoading = true;

      const data = this.form.getRawValue();
      data.pre_content = this.prepareMarkdownForBackend(data.pre_content ?? '');
      data.post_content = this.prepareMarkdownForBackend(data.post_content ?? '');

      this.orderService.previewOrderEmail(this.order.id, this.email.id, data).subscribe({
        next: res => {
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(res);
          this.previewHtmlModalRef = this.modalService.show(this.emailPreviewModalTemplateRef!, {class: 'order-send-email-preview-modal'});
          this.cdr.markForCheck();
          this.previewHtmlModalRef.onHide?.pipe(take(1)).subscribe({
            next: () => {
              this.previewHtml = '';
              this.cdr.markForCheck();
            }
          });
          this.cdr.markForCheck()
        },
        error: err => {
          if (err instanceof NotEnoughPermissionsError) {
            this.toastrService.error(this.translate.instant('shared.misc.not_enough_permissions_error'));
          } else if (err instanceof HttpErrorResponse) {
            this.cdr.detach();
            this.form.markAllAsTouched();
            this.formErrorService.mapErrorToForm(this.form, err);
            this.cdr.reattach();
          } else {
            this.log.error(JSON.stringify(err));
            this.toastrService.error(
              this.translate.instant('guest_management.orders.send_email.preview_error')
            );
          }
          this.previewLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.cdr.detectChanges()
          }, 100)
        },
        complete: () => {
          this.previewLoading = false
          this.cdr.markForCheck();
        }
      });
      this.cdr.markForCheck();
    } else if (this.orderEmail && this.case && this.email && !this.previewLoading && this.emailPreviewModalTemplateRef) {
      this.previewLoading = true;

      const data = this.form.getRawValue();
      data.pre_content = this.prepareMarkdownForBackend(data.pre_content ?? '');
      data.post_content = this.prepareMarkdownForBackend(data.post_content ?? '');

      this.casesService.previewDayProgramDateEmail(this.case.id, this.email.id, data).subscribe({
        next: res => {
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(res);
          this.previewHtmlModalRef = this.modalService.show(this.emailPreviewModalTemplateRef!, {class: 'order-send-email-preview-modal'});
          this.cdr.markForCheck();
          this.previewHtmlModalRef.onHide?.pipe(take(1)).subscribe({
            next: () => {
              this.previewHtml = '';
              this.cdr.markForCheck();
            }
          });
          this.cdr.markForCheck()
        },
        error: err => {
          if (err instanceof NotEnoughPermissionsError) {
            this.toastrService.error(this.translate.instant('shared.misc.not_enough_permissions_error'));
          } else if (err instanceof HttpErrorResponse) {
            this.cdr.detach();
            this.form.markAllAsTouched();
            this.formErrorService.mapErrorToForm(this.form, err);
            this.cdr.reattach();
          } else {
            this.log.error(JSON.stringify(err));
            this.toastrService.error(
              this.translate.instant('guest_management.orders.send_email.preview_error')
            );
          }
          this.previewLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.cdr.detectChanges()
          }, 100)
        },
        complete: () => {
          this.previewLoading = false
          this.cdr.markForCheck();
        }
      });
      this.cdr.markForCheck();
    } else {
      console.error('unsupported preview config passed');
    }
  }

  public submit(): void {
    const data = this.form.getRawValue();
    data.pre_content = this.prepareMarkdownForBackend(data.pre_content ?? '');
    data.post_content = this.prepareMarkdownForBackend(data.post_content ?? '');
    this.submitEvent.emit(data);
  }

  public removeRecipient(recipientType: 'cc' | 'bcc', index: number): void {
    const formControls = (recipientType === 'bcc' ? this.form.controls.bcc_recipients : this.form.controls.cc_recipients) as unknown as FormArray;
    formControls.removeAt(index);
    this.cdr.markForCheck();
  }

  public addRecipient(recipientType: 'cc' | 'bcc'): void {
    const formControls = (recipientType === 'bcc' ? this.form.controls.bcc_recipients : this.form.controls.cc_recipients) as unknown as FormArray;
    formControls.push(
      this.fb.control('')
    )
    this.cdr.markForCheck();
  }

  private prepareMarkdownForBackend(markdown: string): string {
    return markdown.replace(/{{--(.*?)--}}/g, (match, comment) => {
      // Remove backslashes within the comment
      const cleanedComment = comment.replace(/\\/g, '');
      return `{{--${cleanedComment}--}}`;
    });
  }

  public constructor(
    protected readonly cdr: ChangeDetectorRef,
    protected readonly translate: TranslateService,
    protected readonly contentHeaderService: ContentHeaderService,
    protected readonly titleService: TitleService,
    protected readonly localeService: LocaleService,
    private orderService: OrdersService,
    private casesService: CasesService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    private toastrService: ToastrService,
    private formErrorService: FormErrorService,
    private log: NGXLogger,
    private sanitizer: DomSanitizer,
    public emailService: EmailService
  ) {
  }
}
